import React from "react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { TriangleAlert } from "lucide-react";
import {
  PILLARS, SCORE_VALUES, SCORE_LABEL,
  CONTINUITY, VIEW_OPTIONS, PROGRAMMES, TERMS, YEAR_LEVELS,
  isInvalidAttendance,
} from "../lib/sessionForm";

/**
 * Shared session-form body — used by both the public teacher form and the
 * admin paper-session transcription page.
 *
 * Props:
 * - form                  the flat form state
 * - setField(key, value)  update a scalar field
 * - setScore(key, value)  update a descriptor score
 * - schools               list of {id, name, ...} for the School dropdown
 */
export default function SessionFormBody({ form, setField, setScore, schools }) {
  const attInvalid = isInvalidAttendance(form);

  return (
    <>
      {/* -- Session Details -- */}
      <Card title="Session Details">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldWrap label="School" required>
            <Select value={form.school} onValueChange={(v) => setField("school", v)}>
              <SelectTrigger data-testid="field-school" className="h-11">
                <SelectValue placeholder="Select school…" />
              </SelectTrigger>
              <SelectContent>
                {schools.map((s) => (
                  <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                ))}
                <SelectItem value="__other__">School not listed…</SelectItem>
              </SelectContent>
            </Select>
          </FieldWrap>

          {form.school === "__other__" ? (
            <FieldWrap label="School not listed — name" required>
              <Input
                data-testid="field-school-other"
                value={form.school_other}
                onChange={(e) => setField("school_other", e.target.value)}
                placeholder="Type the school name"
                className="h-11"
              />
            </FieldWrap>
          ) : (
            <div />
          )}

          <FieldWrap label="Year Level" required>
            <Select value={form.year_level} onValueChange={(v) => setField("year_level", v)}>
              <SelectTrigger data-testid="field-year-level" className="h-11">
                <SelectValue placeholder="Select year level…" />
              </SelectTrigger>
              <SelectContent>
                {YEAR_LEVELS.map((y) => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldWrap>

          <FieldWrap label="Class" required>
            <Input
              data-testid="field-class"
              value={form.class_name}
              onChange={(e) => setField("class_name", e.target.value)}
              placeholder="e.g. 10OF"
              className="h-11"
            />
          </FieldWrap>

          <FieldWrap label="Teacher Name" required>
            <Input
              data-testid="field-teacher-name"
              value={form.teacher_name}
              onChange={(e) => setField("teacher_name", e.target.value)}
              placeholder="Full name"
              className="h-11"
            />
          </FieldWrap>

          <FieldWrap label="Teacher / Reliever" required>
            <ChipGroup
              testId="field-role"
              options={["Teacher", "Reliever"]}
              value={form.role}
              onChange={(v) => setField("role", v)}
            />
          </FieldWrap>

          <FieldWrap label="Session / Week" required>
            <Input
              data-testid="field-session-week"
              value={form.session_week}
              onChange={(e) => setField("session_week", e.target.value)}
              placeholder="e.g. Week 3"
              className="h-11"
            />
          </FieldWrap>

          <FieldWrap label="Date" required>
            <Input
              data-testid="field-date"
              type="date"
              value={form.date}
              onChange={(e) => setField("date", e.target.value)}
              className="h-11"
            />
          </FieldWrap>

          <FieldWrap label="Term" required>
            <Select value={form.term} onValueChange={(v) => setField("term", v)}>
              <SelectTrigger data-testid="field-term" className="h-11">
                <SelectValue placeholder="Select term…" />
              </SelectTrigger>
              <SelectContent>
                {TERMS.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
              </SelectContent>
            </Select>
          </FieldWrap>

          <FieldWrap label="Programme Type" required span2>
            <ChipGroup
              testId="field-programme"
              options={PROGRAMMES}
              value={form.programme_type}
              onChange={(v) => setField("programme_type", v)}
              wrap
            />
          </FieldWrap>
        </div>
      </Card>

      {/* -- Scoring key -- */}
      <div
        data-testid="scoring-key"
        className="rounded-lg bg-white border border-neutral-200 px-4 py-3 text-sm text-neutral-700 flex flex-wrap items-center gap-x-4 gap-y-1"
      >
        <span className="font-semibold text-neutral-900">Scoring key:</span>
        <span><b className="text-neutral-900">0</b> = Not</span>
        <span className="text-neutral-300">|</span>
        <span><b className="text-neutral-900">3</b> = Inconsistent</span>
        <span className="text-neutral-300">|</span>
        <span><b className="text-neutral-900">5</b> = Mostly</span>
        <span className="text-neutral-300">|</span>
        <span><b className="text-neutral-900">6</b> = Clear</span>
      </div>

      {/* -- Pillars -- */}
      <Card title="Learning Pillars">
        <div className="space-y-6">
          {PILLARS.map((p) => (
            <div key={p.name}>
              <h3 className="font-display text-xl font-semibold text-neutral-900 mb-2">{p.name}</h3>
              <div className="space-y-2">
                {p.descriptors.map((d) => (
                  <div
                    key={d.key}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 py-2 border-b border-neutral-100 last:border-0"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-neutral-900">{d.label}</div>
                      {d.sub && <div className="text-xs text-neutral-500">{d.sub}</div>}
                    </div>
                    <ScoreButtons
                      descriptorKey={d.key}
                      value={form.scores[d.key]}
                      onChange={(v) => setScore(d.key, v)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* -- Attendance -- */}
      <Card title="Attendance">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldWrap label="Expected on roll" required>
            <Input
              data-testid="field-expected"
              type="number"
              min={0}
              inputMode="numeric"
              value={form.expected_on_roll}
              onChange={(e) => setField("expected_on_roll", e.target.value)}
              className="h-11"
            />
          </FieldWrap>
          <FieldWrap label="Present learners" required>
            <Input
              data-testid="field-present"
              type="number"
              min={0}
              inputMode="numeric"
              value={form.present_learners}
              onChange={(e) => setField("present_learners", e.target.value)}
              className="h-11"
            />
          </FieldWrap>
        </div>
        {attInvalid && (
          <div
            data-testid="attendance-warning"
            className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2 text-sm text-amber-900"
          >
            <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              Present learners is higher than Expected on roll — this session will be flagged as{" "}
              <b>Invalid Attendance</b> rather than scored, since it likely means something was entered wrong.
              Double-check the two figures, or continue if this is correct.
            </div>
          </div>
        )}
      </Card>

      {/* -- Continuity + View -- */}
      <Card title="Session Continuity & View">
        <FieldWrap label="How steady or disrupted was the session overall?" required>
          <ChipGroup
            testId="field-continuity"
            options={CONTINUITY}
            value={form.continuity}
            onChange={(v) => setField("continuity", v)}
            wrap
          />
        </FieldWrap>
        <div className="h-4" />
        <FieldWrap label="How clearly were you able to observe the overall session?" required>
          <ChipGroup
            testId="field-session-view"
            options={VIEW_OPTIONS}
            value={form.session_view}
            onChange={(v) => setField("session_view", v)}
            wrap
          />
        </FieldWrap>
      </Card>

      {/* -- Comment -- */}
      <Card
        title="Collective Comment"
        hint="Class-level only — never about individual students."
      >
        <Textarea
          data-testid="field-comment"
          value={form.comment}
          onChange={(e) => setField("comment", e.target.value)}
          placeholder="e.g. 'The class showed strong collective energy today' — not 'Jordan struggled to focus.'"
          rows={4}
          className="resize-none"
        />
        <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
          Reminder: CPI records class-level participation conditions within this session window.
          Do not use this field to describe individual students.
        </div>
      </Card>
    </>
  );
}

export function Card({ title, hint, children }) {
  return (
    <section className="rounded-xl bg-white border border-neutral-200 p-4 sm:p-6">
      <div className="mb-4">
        <h2 className="font-display text-lg sm:text-xl font-semibold text-neutral-900">{title}</h2>
        {hint && <div className="text-xs text-neutral-500 mt-1">{hint}</div>}
      </div>
      {children}
    </section>
  );
}

function FieldWrap({ label, required, span2, children }) {
  return (
    <div className={span2 ? "sm:col-span-2" : ""}>
      <Label className="text-sm text-neutral-700 mb-1 block">
        {label}
        {required && <span className="text-neutral-400 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}

function ChipGroup({ options, value, onChange, testId, wrap = false }) {
  return (
    <div data-testid={testId} className={`flex ${wrap ? "flex-wrap" : ""} gap-2`}>
      {options.map((o) => {
        const selected = value === o;
        return (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            data-testid={`${testId}-opt-${o.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            aria-pressed={selected}
            className={
              "min-h-11 px-4 rounded-full border text-sm font-medium transition-colors " +
              (selected
                ? "bg-neutral-900 text-white border-neutral-900"
                : "bg-white text-neutral-800 border-neutral-300 hover:border-neutral-500")
            }
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

function ScoreButtons({ descriptorKey, value, onChange }) {
  return (
    <div
      data-testid={`score-${descriptorKey}`}
      className="flex gap-2 shrink-0"
      role="radiogroup"
      aria-label={`Score for ${descriptorKey}`}
    >
      {SCORE_VALUES.map((v) => {
        const selected = value === v;
        return (
          <button
            key={v}
            type="button"
            aria-pressed={selected}
            title={SCORE_LABEL[v]}
            onClick={() => onChange(v)}
            data-testid={`score-${descriptorKey}-${v}`}
            className={
              "w-12 h-11 rounded-lg border text-base font-semibold transition-colors " +
              (selected
                ? "bg-neutral-900 text-white border-neutral-900 shadow-sm"
                : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500")
            }
          >
            {v}
          </button>
        );
      })}
    </div>
  );
}
